import {Model3dViewer} from "./model3dViewer";
import {closeModal, showModal} from "../../../default/global/js/modal";

/**
 * Step of one click on the zoom buttons.
 */
const ZOOM_STEP = 1.25;

/**
 * The viewer that is open at the moment. A WebGL context is a limited
 * resource, so the page keeps one inline viewer and one overlay viewer at
 * most. A new viewer closes the one before it.
 */
const openViewers = {
    inline: null,
    overlay: null
};

/**
 * The element that holds the viewer of each slot.
 *
 * A viewer that closes takes its canvas with it and leaves the element
 * behind. The element must then go back to the state before the load, or the
 * slide stays empty and offers no way to load the model again.
 */
const openRoots = {
    inline: null,
    overlay: null
};

/**
 * Show the state of a model in its status line.
 *
 * @param {HTMLElement} root Element that holds the status line and the texts.
 * @param {string} state One of "loading", "ready" and "error".
 * @param {number} progress Value from 0 to 1.
 */
function showStatus(root, state, progress) {
    const status = root.querySelector(".js-model3dStatus");
    if (!status) {
        return;
    }

    if (state === "ready") {
        status.textContent = "";
        status.hidden = true;
        root.classList.remove("is-loading", "has-error");
        return;
    }

    status.hidden = false;
    if (state === "error") {
        status.textContent = root.dataset.textError || "";
        root.classList.remove("is-loading");
        root.classList.add("has-error");
        return;
    }

    const loadingText = root.dataset.textLoading || "";
    const percent = Math.round(progress * 100);
    status.textContent = percent > 0 ? loadingText + " " + percent + " %" : loadingText;
    root.classList.add("is-loading");
    root.classList.remove("has-error");
}

/**
 * Build a viewer inside an element and load a model into it.
 *
 * @param {HTMLElement} root Element with the stage and the texts.
 * @param {string} url Address of the model.
 * @param {Object} options
 * @param {string} options.slot Either "inline" or "overlay".
 * @param {function(Model3dViewer)} [options.onCameraChange]
 * @return {Model3dViewer|null} The new viewer, or null without a stage.
 */
function createViewer(root, url, options) {
    const stage = root.querySelector(".js-model3dStage");
    if (!stage) {
        return null;
    }

    closeViewer(options.slot);

    const viewer = new Model3dViewer(stage, {
        onProgress: (state, progress) => showStatus(root, state, progress),
        onCameraChange: () => {
            if (options.onCameraChange) {
                options.onCameraChange(viewer);
            }
        }
    });

    openViewers[options.slot] = viewer;
    openRoots[options.slot] = root;
    root.classList.add("is-active");

    viewer.load(url).catch(() => {
        // The viewer already showed the error through onProgress. The model
        // stays empty, and the page keeps working.
    });

    return viewer;
}

/**
 * Close the viewer of one slot and give its WebGL context back.
 *
 * The element of the viewer goes back to the state before the load. A visitor
 * who steps from one model to another and then back therefore finds the
 * button that starts the download again.
 *
 * @param {string} slot Either "inline" or "overlay".
 */
function closeViewer(slot) {
    const viewer = openViewers[slot];
    if (viewer) {
        viewer.dispose();
        openViewers[slot] = null;
    }

    const root = openRoots[slot];
    if (root) {
        resetRoot(root);
        openRoots[slot] = null;
    }
}

/**
 * Put the element of a viewer back into the state before the load.
 *
 * @param {HTMLElement} root Element that held the viewer.
 */
function resetRoot(root) {
    root.classList.remove("is-active", "is-loading", "has-error");

    const status = root.querySelector(".js-model3dStatus");
    if (status) {
        status.textContent = "";
        status.hidden = true;
    }

    // The overlay has no start button. Only a model in the image gallery
    // waits for a click before it loads.
    const start = root.querySelector(".js-model3dStart");
    if (start) {
        start.hidden = false;
    }
}

/**
 * Run a function when the modal closes.
 *
 * The modal takes the class "open" away when it closes, whatever closed it:
 * the close button, a click beside the dialog or the escape key. Watching the
 * class therefore catches every way.
 *
 * @param {HTMLElement} modal
 * @param {function()} callback
 */
function onModalClose(modal, callback) {
    const observer = new MutationObserver(() => {
        if (!modal.classList.contains("open")) {
            callback();
        }
    });
    observer.observe(modal, {attributes: true, attributeFilter: ["class"]});
}

/**
 * Wire the 3D viewer of the overlay.
 *
 * @param {HTMLElement} modal The modal element.
 * @return {Object|null} An object with an open(url) method.
 */
function initOverlay(modal) {
    const root = modal.querySelector(".js-model3dViewer");
    if (!root) {
        return null;
    }

    const azimuthSlider = root.querySelector(".js-model3dAzimuth");
    const polarSlider = root.querySelector(".js-model3dPolar");
    const modelButtons = root.querySelectorAll(".js-model3dSelect");
    let syncingSliders = false;

    const syncSliders = (viewer) => {
        syncingSliders = true;
        if (azimuthSlider) {
            azimuthSlider.value = String(Math.round(viewer.getAzimuth()));
        }
        if (polarSlider) {
            polarSlider.value = String(Math.round(viewer.getPolar()));
        }
        syncingSliders = false;
    };

    const open = (url) => {
        const viewer = createViewer(root, url, {
            slot: "overlay",
            onCameraChange: syncSliders
        });
        if (!viewer) {
            return;
        }

        for (const button of modelButtons) {
            button.classList.toggle("is-active", button.dataset.modelUrl === url);
        }

        const fitToStage = () => {
            // The stage has its final size only after the modal is visible.
            viewer.applySize();
            viewer.frameModel(false);
            syncSliders(viewer);
        };

        // A click on a model button comes from inside the open modal. A second
        // showModal() call would count the modal as its own parent modal and
        // add one more close listener on each click.
        if (modal.classList.contains("open")) {
            fitToStage();
            return;
        }

        showModal(modal, () => {
            // showModal() adds the class "open" after a delay. When the visitor
            // closed the modal in that time, the viewer is already gone, and
            // the modal would stay open and empty. Close it again.
            if (openViewers.overlay !== viewer) {
                closeModal(modal);
                return;
            }
            fitToStage();
        });
    };

    if (azimuthSlider) {
        azimuthSlider.addEventListener("input", () => {
            if (!syncingSliders && openViewers.overlay) {
                openViewers.overlay.setAzimuth(Number(azimuthSlider.value));
            }
        });
    }

    if (polarSlider) {
        polarSlider.addEventListener("input", () => {
            if (!syncingSliders && openViewers.overlay) {
                openViewers.overlay.setPolar(Number(polarSlider.value));
            }
        });
    }

    const zoomIn = root.querySelector(".js-model3dZoomIn");
    if (zoomIn) {
        zoomIn.addEventListener("click", () => {
            if (openViewers.overlay) {
                openViewers.overlay.zoomBy(1 / ZOOM_STEP);
            }
        });
    }

    const zoomOut = root.querySelector(".js-model3dZoomOut");
    if (zoomOut) {
        zoomOut.addEventListener("click", () => {
            if (openViewers.overlay) {
                openViewers.overlay.zoomBy(ZOOM_STEP);
            }
        });
    }

    const reset = root.querySelector(".js-model3dReset");
    if (reset) {
        reset.addEventListener("click", () => {
            if (openViewers.overlay) {
                openViewers.overlay.reset();
            }
        });
    }

    for (const button of modelButtons) {
        button.addEventListener("click", () => open(button.dataset.modelUrl));
    }

    // A model holds much data. Give the memory back as soon as the overlay
    // closes, instead of keeping it until the visitor leaves the page.
    onModalClose(modal, () => closeViewer("overlay"));

    return {open: open};
}

/**
 * Wire the 3D models that stand in the image gallery.
 *
 * A model is loaded only after a click. The files are large, and an item card
 * must not download one before the visitor asks for the 3D view.
 *
 * @param {NodeListOf<HTMLElement>} inlineViewers
 * @param {Object|null} overlay
 */
function initInlineViewers(inlineViewers, overlay) {
    for (const root of inlineViewers) {
        const url = root.dataset.modelUrl;
        if (!url) {
            continue;
        }

        const start = root.querySelector(".js-model3dStart");
        if (start) {
            start.addEventListener("click", () => {
                start.hidden = true;
                createViewer(root, url, {slot: "inline"});
            });
        }

        const fullscreen = root.querySelector(".js-model3dFullscreen");
        if (fullscreen && overlay) {
            fullscreen.addEventListener("click", () => overlay.open(url));
        }
    }
}

/**
 * Start the 3D view of the item card.
 *
 * The function does nothing when the item card holds no model.
 */
export function initModel3d() {
    const modal = document.getElementById("model3dModal");
    const overlay = modal ? initOverlay(modal) : null;

    initInlineViewers(document.querySelectorAll(".js-model3dInline"), overlay);

    // The round button on the item image opens the first model. The models of
    // the gallery each have their own button beside the image.
    const openButton = document.querySelector(".js-model3dOpen");
    const firstModel = document.querySelector(".js-model3dInline");
    if (openButton && overlay && firstModel) {
        openButton.addEventListener("click", () => overlay.open(firstModel.dataset.modelUrl));
    }
}
